import { AlertService } from "../../common-services/Alert/AlertService";
import { FinancialStatus } from "../../enums/FinancialStatus";
import { OrderAlertType } from "../../enums/OrderAlertType";
import { SeparationType } from "../../enums/SeparationType";
import { StateOrderIdentification } from "../../enums/StateOrderIdentification";
import { StatePrescriptionIdentification } from "../../enums/StatePrescriptionIdentification";
import { CurrentState } from "../../models/CurrentState";
import { OrderForListing } from "../../models/OrderForListing";
import { RecipeNotificationConstants } from '../../models/RecipeNotification';
import { State } from "../../models/State";
import { StatePrescription } from "../../models/StatePrescription";
import { orderTags } from "../../resources/orderResources";
import { strings } from "../../resources/strings";
import { GetCountNotificationsByOrdersInput } from '../RecipesNotifications/GetCountNotificationsByOrders/GetCountNotificationsByOrdersInput';
import { GetCountNotificationsByOrdersService } from '../RecipesNotifications/GetCountNotificationsByOrders/GetCountNotificationsByOrdersService';
import { OrderStateOutput } from "./FilterOrder/OrderStateOutput";
import { GetCurrentStatesByOrdersInput } from "./GetCurrentStatesByOrders/GetCurrentStatesByOrdersInput";
import { GetCurrentStatesByOrdersService } from "./GetCurrentStatesByOrders/GetCurrentStatesByOrdersService";

export class OrderForListingService {

  constructor(
    private alertService: AlertService,
    private getCurrentStatesByOrdersService: GetCurrentStatesByOrdersService,
    private states: State[],
    private deliveryStates: State[],
    private statesPrescription: StatePrescription[],
    private getCountNotificationsByOrdersService: GetCountNotificationsByOrdersService
  ) {
  }

  getSelectedOrders = (ordersForListing: OrderForListing[]) =>
    ordersForListing?.filter(xo => xo._selected) || [];

  getCountOfCancelledOrders = (selectedOrders: OrderForListing[]) =>
    selectedOrders.filter(xo => xo.order.shopifyFormatedCancelledAt?.length > 0).length;

  getCountOfCancelledAndNotClosedOrders = (selectedOrders: OrderForListing[]) =>
    selectedOrders.filter(xo => xo.order.shopifyFormatedCancelledAt?.length > 0 && !(xo.order.shopifyFormatedClosedAt?.length)).length;

  getCountOfClosedOrders = (selectedOrders: OrderForListing[]) =>
    selectedOrders.filter(xo => xo.order.shopifyFormatedClosedAt?.length > 0).length;

  getCountOfExternallyChangedOrders = (selectedOrders: OrderForListing[]) =>
    selectedOrders.filter(xo => xo._externallyChanged).length;

  getCountOfClosedAndNotCancelledOrders = (selectedOrders: OrderForListing[]) =>
    selectedOrders.filter(xo => xo.order.shopifyFormatedClosedAt?.length > 0 && !(xo.order.shopifyFormatedCancelledAt?.length)).length;

  getUserConfirmationBeforeShowModalToUpdateStateForSelectedOrders = async (selectedOrders: OrderForListing[]) => {
    const countCancelledOrders = this.getCountOfCancelledAndNotClosedOrders(selectedOrders);
    const countClosedOrders = this.getCountOfClosedOrders(selectedOrders);
    const countExternallyChanged = this.getCountOfExternallyChangedOrders(selectedOrders);

    if (countCancelledOrders === 0 && countClosedOrders === 0 && countExternallyChanged === 0)
      return true;

    let message = '';
    if (countCancelledOrders > 0) {
      message += countCancelledOrders > 1
        ? `${countCancelledOrders} pedidos selecionados estão cancelados e não serão alterados. `
        : `Um pedido selecionado está cancelado e não será alterado. `;
    }
    if (countClosedOrders > 0) {
      if (message) message += '<hr>';
      message += countClosedOrders > 1
        ? `${countClosedOrders} pedidos selecionados estão fechados e não serão alterados. `
        : `Um pedido selecionado está fechado e não será alterado. `;
    }
    if (countExternallyChanged > 0) {
      if (message) message += '<hr>';
      message += countExternallyChanged > 1
        ? `${countClosedOrders} pedidos selecionados estão com tag de alteração externa e não serão alterados. `
        : `Um pedido selecionado está com tag de alteração externa e não será alterado. `;
    }

    const countIneligibleOrders = countCancelledOrders + countClosedOrders + countExternallyChanged;

    if (countIneligibleOrders >= selectedOrders.length) {
      message += '<br><br><strong>Todos os pedidos selecionados possuem restrições de alteração e portanto não podem ser alterados.</strong>';
      await this.alertService.warningHtml(message);
      return false;
    } else {
      message += '<br><br><strong>Deseja continuar?</strong>';
      const response = await this.alertService.confirmHtml(message);
      return response.isConfirmed;
    }
  }

  getPermissionBeforeShowModalToUpdateStateForSelectedOrders = async (selectedOrders: OrderForListing[]) => {
    const countCancelledOrders = this.getCountOfCancelledOrders(selectedOrders);
    const countClosedOrders = this.getCountOfClosedAndNotCancelledOrders(selectedOrders);
    const countExternallyChangedOrders = this.getCountOfExternallyChangedOrders(selectedOrders);

    if (countCancelledOrders === 0 && countClosedOrders === 0 && countExternallyChangedOrders === 0)
      return true;

    let message = '';

    if (countCancelledOrders > 0) {
      message += countCancelledOrders > 1
        ? `${countCancelledOrders} pedidos selecionados estão cancelados e não podem ser alterados. `
        : `Um pedido selecionado está cancelado e não pode ser alterado. `;
    }

    if (countClosedOrders > 0) {
      if (message) message += '<hr>';
      message += countClosedOrders > 1
        ? `${countClosedOrders} pedidos selecionados estão fechados e não podem ser alterados. `
        : `Um pedido selecionado está fechado e não pode ser alterado. `;
    }

    if (countExternallyChangedOrders > 0) {
      if (message) message += '<hr>';
      message += countExternallyChangedOrders > 1
        ? `${countExternallyChangedOrders} pedidos selecionados estão com tag de alteração externa e não podem ser alterados. `
        : `Um pedido selecionado está com tag de alteração externa e não pode ser alterado. `;
    }

    const countIneligibleOrders = countCancelledOrders + countClosedOrders + countExternallyChangedOrders;

    if (countIneligibleOrders >= selectedOrders.length) {
      message += '<br><br><strong>Todos os pedidos selecionados possuem restrições de alteração e portanto não podem ser alterados.</strong>';
    } else {
      message += '<br><br><strong>Parte dos pedidos selecionados possuem restrições de alteração e portanto não podem ser alterados.</strong>';
    }

    if (message) {
      message += '<br><br>Clique em "Atualizar Lista" e remova da seleção os pedidos com restrições.'
      await this.alertService.warningHtml(message);
      return false;
    }

    return true;
  }

  getEligibleOrdersToUpdate = async (selectedOrders: OrderForListing[]) => {
    const closedOrders: CurrentState[] = [];
    const cancelledOrders: CurrentState[] = [];
    const ordersWithChangedState: CurrentState[] = [];
    const ordersExternallyChanged: CurrentState[] = [];
    const elibibleOrders: CurrentState[] = [];

    const listStates = selectedOrders?.map(selectedOrder => {
      const currentState = new CurrentState();
      currentState.orderId = selectedOrder.order.id;
      currentState.number = selectedOrder.order.number;
      currentState.currentStateLogId = selectedOrder.stateLog?.currentStateId || null;
      currentState.currentStatePrescriptionLogId = selectedOrder.statePrescriptionLog?.currentStateId || null;
      currentState.isClosed = selectedOrder.order.shopifyFormatedClosedAt?.length > 0;
      currentState.isCancelled = selectedOrder.order.shopifyFormatedCancelledAt?.length > 0;
      currentState.externallyChanged = selectedOrder._externallyChanged;
      return currentState;
    }) || [];

    const getCurrentStatesByOrdersInput = new GetCurrentStatesByOrdersInput();
    getCurrentStatesByOrdersInput.ordersId = listStates.map(st => st.orderId);
    const getCurrentStatesByOrdersResult = await this.getCurrentStatesByOrdersService.handle(getCurrentStatesByOrdersInput);
    if (getCurrentStatesByOrdersResult?.value) {
      const getCurrentStatesByOrdersOutput = getCurrentStatesByOrdersResult.value;
      listStates.forEach(item => {
        const currentState = getCurrentStatesByOrdersOutput.currentStates.find(cs => cs.orderId === item.orderId);
        const isClosed = currentState?.isClosed || item.isClosed;
        const isCancelled = currentState?.isCancelled || item.isCancelled;
        const stateHasChanged = currentState?.currentStateLogId !== item.currentStateLogId;
        if (item.externallyChanged) {
          ordersExternallyChanged.push(item);
        } else if (isClosed) {
          closedOrders.push(item);
        } else if (isCancelled) {
          cancelledOrders.push(item);
        } else if (stateHasChanged) {
          ordersWithChangedState.push(item);
        } else {
          elibibleOrders.push(item);
        }
      });
    }

    const countOrdersWithChangedState = ordersWithChangedState.length;
    if (countOrdersWithChangedState > 0) {
      let message = countOrdersWithChangedState > 1
        ? `${countOrdersWithChangedState} pedidos já não estão mais no estado visualizado.`
        : `Um pedido já não está mais no estado visualizado.`;
      message += ` Clique em "Atualizar Lista" e remova da seleção os pedidos alterados.`
      this.alertService.warning(message);
      return null;
    }

    const countCancelledOrders = cancelledOrders.length;
    const countClosedOrders = closedOrders.length;
    const countExternallyChangedOrders = ordersExternallyChanged.length;

    if (countCancelledOrders > 0 || countClosedOrders > 0 || countExternallyChangedOrders > 0) {
      if (elibibleOrders?.length > 0) {
        let message = '';
        if (countCancelledOrders > 0) {
          message += countCancelledOrders > 1
            ? `${countCancelledOrders} pedidos selecionados estão cancelados e ao continuar estes pedidos não serão alterados. `
            : `Um pedido selecionado está cancelado e ao continuar este pedido não será alterado. `;
        }
        if (countClosedOrders > 0) {
          if (message) message += '<hr>';
          message += countClosedOrders > 1
            ? `${countClosedOrders} pedidos selecionados estão fechados e ao continuar estes pedidos não serão alterados. `
            : `Um pedido selecionado está fechado e ao continuar este pedido não será alterado. `;
        }
        if (countExternallyChangedOrders > 0) {
          if (message) message += '<hr>';
          message += countExternallyChangedOrders > 1
            ? `${countExternallyChangedOrders} pedidos selecionados estão com tag de alteração externa e ao continuar estes pedidos não serão alterados. `
            : `Um pedido selecionado está com tag de alteração externa e ao continuar este pedido não será alterado. `;
        }
        message += '<br><br><strong>Deseja realmente continuar?</strong>';
        const response = await this.alertService.confirmHtml(message);
        if (!response.isConfirmed) return null;
      } else {
        let message = '';
        if (countCancelledOrders > 0) {
          message += countCancelledOrders > 1
            ? `${countCancelledOrders} pedidos selecionados estão cancelados e não podem ser alterados. `
            : `Um pedido selecionado está cancelado e não pode ser alterado. `;
        }
        if (countClosedOrders > 0) {
          if (message) message += '<hr>';
          message += countClosedOrders > 1
            ? `${countClosedOrders} pedidos selecionados estão fechados e não podem ser alterados. `
            : `Um pedido selecionado está fechado e não pode ser alterado. `;
        }
        if (countExternallyChangedOrders > 0) {
          if (message) message += '<hr>';
          message += countExternallyChangedOrders > 1
            ? `${countExternallyChangedOrders} pedidos selecionados estão com tag de alteração externa e não podem ser alterados. `
            : `Um pedido selecionado está com tag de alteração externa e não poe ser alterado. `;
        }
        message += '<br><br><strong>Todos os pedidos selecionados possuem restrições de alteração e portanto não podem ser alterados.</strong>';
        await this.alertService.warningHtml(message);
        return null;
      }
    }

    return elibibleOrders;
  }

  getEligibleOrdersToUpdatePrescriptionState = async (selectedOrders: OrderForListing[], statePrescriptionId: StatePrescriptionIdentification) => {
    const closedOrders: CurrentState[] = [];
    const cancelledOrders: CurrentState[] = [];
    const ordersWithChangedState: CurrentState[] = [];
    const ordersWithoutRecipes: CurrentState[] = [];
    const ordersWithRecipeNotApproved: CurrentState[] = [];
    const ordersExternallyChanged: CurrentState[] = [];
    const elibibleOrders: CurrentState[] = [];

    const listStates = selectedOrders?.map(selectedOrder => {
      const currentState = new CurrentState();
      currentState.orderId = selectedOrder.order.id;
      currentState.number = selectedOrder.order.number;
      currentState.currentStatePrescriptionLogId = selectedOrder.statePrescriptionLog?.currentStateId || null;
      currentState.isCancelled = selectedOrder.order.shopifyFormatedCancelledAt?.length > 0;
      currentState.isClosed = !currentState.isCancelled && selectedOrder.order.shopifyFormatedClosedAt?.length > 0;
      currentState.externallyChanged = selectedOrder._externallyChanged;
      return currentState;
    }) || [];

    const newStatePrescriptionRequireRecipe =
      statePrescriptionId === StatePrescriptionIdentification.ReceitaPendente ||
      statePrescriptionId === StatePrescriptionIdentification.SolicitarLente ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoLente ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemInterna ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemExterna ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoConferencia ||
      statePrescriptionId === StatePrescriptionIdentification.Ok;
    const newStatePrescriptionRequireApprovedRecipe =
      statePrescriptionId === StatePrescriptionIdentification.SolicitarLente ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoLente ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemInterna ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemExterna ||
      statePrescriptionId === StatePrescriptionIdentification.AguardandoConferencia ||
      statePrescriptionId === StatePrescriptionIdentification.Ok;

    const getCurrentStatesByOrdersInput = new GetCurrentStatesByOrdersInput();
    getCurrentStatesByOrdersInput.ordersId = listStates.map(st => st.orderId);
    const getCurrentStatesByOrdersResult = await this.getCurrentStatesByOrdersService.handle(getCurrentStatesByOrdersInput);
    if (getCurrentStatesByOrdersResult?.value) {
      const getCurrentStatesByOrdersOutput = getCurrentStatesByOrdersResult.value;
      listStates.forEach(item => {
        const currentState = getCurrentStatesByOrdersOutput.currentStates.find(cs => cs.orderId === item.orderId);
        const isCancelled = currentState?.isCancelled || item.isCancelled;
        const isClosed = !isCancelled && (currentState?.isClosed || item.isClosed);
        const stateHasChanged = currentState?.currentStatePrescriptionLogId !== item.currentStatePrescriptionLogId;
        let orderWithoutRecipe = false;
        let orderWithRecipeNotApproved = false;
        if (newStatePrescriptionRequireRecipe)
          orderWithoutRecipe = !currentState?.numberOfRecipes || currentState?.numberOfRecipes === 0;
        if (!orderWithoutRecipe && newStatePrescriptionRequireApprovedRecipe)
          orderWithRecipeNotApproved = !currentState?.allRecipesApproved;
        if (item.externallyChanged) {
          ordersExternallyChanged.push(item);
        } else if (isClosed) {
          closedOrders.push(item);
        } else if (isCancelled) {
          cancelledOrders.push(item);
        } else if (stateHasChanged) {
          ordersWithChangedState.push(item);
        } else if (orderWithoutRecipe) {
          ordersWithoutRecipes.push(item);
        } else if (orderWithRecipeNotApproved) {
          ordersWithRecipeNotApproved.push(item);
          elibibleOrders.push(item);
        } else {
          elibibleOrders.push(item);
        }
      });
    }

    let message = '';

    const countOrdersWithChangedState = ordersWithChangedState.length;
    if (countOrdersWithChangedState > 0) {
      if (message) message += '<hr>';
      message += countOrdersWithChangedState > 1
        ? `${countOrdersWithChangedState} pedidos já não estão mais no estado visualizado.`
        : `Um pedido já não está mais no estado visualizado.`;
    }

    const countCancelledOrders = cancelledOrders.length;
    if (countCancelledOrders > 0) {
      if (message) message += '<hr>';
      message += countCancelledOrders > 1
        ? `${countCancelledOrders} pedidos selecionados estão cancelados. `
        : `Um pedido selecionado está cancelado. `;
    }

    const countClosedOrders = closedOrders.length;
    if (countClosedOrders > 0) {
      if (message) message += '<hr>';
      message += countClosedOrders > 1
        ? `${countClosedOrders} pedidos selecionados estão fechados. `
        : `Um pedido selecionado está fechado. `;
    }

    const countOrdersWithoutRecipes = ordersWithoutRecipes.length;
    if (countOrdersWithoutRecipes > 0) {
      if (message) message += '<hr>';
      message += countOrdersWithoutRecipes > 1
        ? `${countOrdersWithoutRecipes} pedidos ainda não possuem receita.`
        : `Um pedido ainda não possui receita.`;
    }

    if (message) {
      if (message) message += '<hr>';
      message += `Clique em "Atualizar Lista" e remova da seleção os pedidos alterados.`
      await this.alertService.warningHtml(message);
      return null;
    }

    message = '';

    const countOrdersWithRecipeNotApproved = ordersWithRecipeNotApproved.length;
    if (countOrdersWithRecipeNotApproved > 0) {
      if (message) message += '<hr>';
      message += countOrdersWithRecipeNotApproved > 1
        ? `${countOrdersWithRecipeNotApproved} pedidos até possuem receita, mas sem aprovação.`
        : `Um pedido até possui receita, mas sem aprovação.`;
    }

    const countExternallyChangedOrders = ordersExternallyChanged.length;
    if (countExternallyChangedOrders > 0) {
      if (message) message += '<hr>';
      message += countExternallyChangedOrders > 1
        ? `${countExternallyChangedOrders} pedidos estão com tag de alteração externa e não podem ser alterados.`
        : `Um pedido está com tag de alteração externa e não pode ser alterado.`;
    }

    if (message) {
      if (message) message += '<hr>';
      message += `Deseja mesmo assim continuar?`
      const response = await this.alertService.confirmHtml(message);
      if (!response.isConfirmed) return null;
    }

    return elibibleOrders;
  }

  getEligibleOrdersToNotifyCollectRecipeAndDNPs = async (selectedOrders: OrderForListing[]) => {
    const ordersWithoutCustomer: CurrentState[] = [];
    const ordersWithCustomerButWithoutContact: CurrentState[] = [];
    const ordersWithCustomerAndWithContact: CurrentState[] = [];
    const ordersWithStateIneligibleForNotification: CurrentState[] = [];
    const ordersWithNumberOfNotificationsOnLimit: CurrentState[] = [];
    const elibibleOrders: CurrentState[] = [];

    selectedOrders.forEach(xo => {
      const customer = xo.order?.customer;
      if (!customer) {
        ordersWithoutCustomer.push({
          orderId: xo.order.id,
          number: xo.order.number,
          separationType: xo.order.separationType,
          isCancelled: false,
          isClosed: false
        });
      }
    });

    selectedOrders.forEach(xo => {
      const customer = xo.order?.customer;
      const email = customer?.email ? true : false;
      const phone = customer?.phone ? true : false;
      if (customer && (!email && !phone)) {
        ordersWithCustomerButWithoutContact.push({
          orderId: xo.order.id,
          number: xo.order.number,
          separationType: xo.order.separationType,
          isCancelled: false,
          isClosed: false
        });
      }
    });

    selectedOrders.forEach(xo => {
      const customer = xo.order?.customer;
      const email = customer?.email ? true : false;
      const phone = customer?.phone ? true : false;
      if (customer && (email || phone)) {
        ordersWithCustomerAndWithContact.push({
          orderId: xo.order.id,
          number: xo.order.number,
          separationType: xo.order.separationType,
          isCancelled: false,
          isClosed: false
        });
      }
    });

    if (ordersWithCustomerAndWithContact.length === 0) {
      let message = `Nenhum pedido com cliente e contato para poder notificar.`;
      this.alertService.warning(message);
      return null;
    }

    const getCountNotificationsByOrdersInput = new GetCountNotificationsByOrdersInput();
    getCountNotificationsByOrdersInput.orderIds = ordersWithCustomerAndWithContact.map(st => st.orderId);
    const getCountNotificationsByOrdersServiceResult = await this.getCountNotificationsByOrdersService.handle(getCountNotificationsByOrdersInput);
    if (getCountNotificationsByOrdersServiceResult?.value) {
      const getCountNotificationsByOrdersOutput = getCountNotificationsByOrdersServiceResult.value;
      getCountNotificationsByOrdersOutput.list.forEach(countByOrder => {
        if (countByOrder.countPendingAndSuccess >= RecipeNotificationConstants.LimitNumberOfCustomerNotifications) {
          const selectedOrder = selectedOrders.find(so => so.order.id === countByOrder.orderId);
          if (selectedOrder)
            ordersWithNumberOfNotificationsOnLimit.push({
              orderId: selectedOrder.order.id,
              number: selectedOrder.order.number,
              separationType: selectedOrder.order.separationType,
              isCancelled: false,
              isClosed: false,
              numberOfNotifications: countByOrder.countPendingAndSuccess
            });
        }
      });
    }

    const getCurrentStatesByOrdersInput = new GetCurrentStatesByOrdersInput();
    getCurrentStatesByOrdersInput.ordersId = ordersWithCustomerAndWithContact.map(st => st.orderId);
    const getCurrentStatesByOrdersResult = await this.getCurrentStatesByOrdersService.handle(getCurrentStatesByOrdersInput);
    if (getCurrentStatesByOrdersResult?.value) {
      const getCurrentStatesByOrdersOutput = getCurrentStatesByOrdersResult.value;
      getCurrentStatesByOrdersOutput.currentStates.forEach(currentState => {
        const statePrescriptionIsEligibleForNotification =
          !currentState.currentStatePrescriptionLogId ||
          currentState.currentStatePrescriptionLogId === +StatePrescriptionIdentification.SemReceita;
        const stateOrderIsEligibleForNotification =
          !currentState.currentStateLogId ||
          currentState.currentStateLogId === +StateOrderIdentification.PendenteRegistroOmie ||
          currentState.currentStateLogId === +StateOrderIdentification.AguardandoFaturamento ||
          currentState.currentStateLogId === +StateOrderIdentification.AguardandoSeparacao ||
          currentState.currentStateLogId === +StateOrderIdentification.AguardandoPacking;
        if (!statePrescriptionIsEligibleForNotification || !stateOrderIsEligibleForNotification) {
          ordersWithStateIneligibleForNotification.push(currentState);
        }
      });
    }

    const countSelectedOrders = selectedOrders.length;
    const countOrdersWithoutCustomer = ordersWithoutCustomer.length;
    const countOrdersWithCustomerButWithoutContact = ordersWithCustomerButWithoutContact.length;
    const countOrdersWithStateIneligibleForNotification = ordersWithStateIneligibleForNotification.length;
    const countOrdersWithNumberOfNotificationsOnLimit = ordersWithNumberOfNotificationsOnLimit.length;

    selectedOrders.forEach(xo => {
      const orderIsWithoutCustomer = countOrdersWithoutCustomer > 0 &&
        ordersWithoutCustomer.some(x => x.orderId === xo.order.id);
      const orderIsWithCustomerButWithoutContact = countOrdersWithCustomerButWithoutContact > 0 &&
        ordersWithCustomerButWithoutContact.some(x => x.orderId === xo.order.id);
      const orderIsWithStateIneligibleForNotification = countOrdersWithStateIneligibleForNotification > 0 &&
        ordersWithStateIneligibleForNotification.some(x => x.orderId === xo.order.id);
      const orderIsWithNumberOfNotificationsOnLimit = countOrdersWithNumberOfNotificationsOnLimit > 0 &&
        ordersWithNumberOfNotificationsOnLimit.some(x => x.orderId === xo.order.id);
      if (
        !orderIsWithoutCustomer && !orderIsWithCustomerButWithoutContact &&
        !orderIsWithStateIneligibleForNotification && !orderIsWithNumberOfNotificationsOnLimit
      ) {
        elibibleOrders.push({
          orderId: xo.order.id,
          number: xo.order.number,
          separationType: xo.order.separationType,
          isCancelled: false,
          isClosed: false
        });
      }
    });

    const countElibibleOrders = elibibleOrders.length;

    if (
      countOrdersWithoutCustomer > 0 || countOrdersWithCustomerButWithoutContact > 0 ||
      countOrdersWithStateIneligibleForNotification || countOrdersWithNumberOfNotificationsOnLimit > 0
    ) {
      let message = '';
      if (countOrdersWithoutCustomer > 0) {
        if (message) message += '<hr>';
        message += countOrdersWithoutCustomer > 1
          ? `${countOrdersWithoutCustomer} pedidos estão sem cliente e não tem como notificar.`
          : `Um pedido está sem cliente e não tem como notificar.`;
      }
      if (countOrdersWithCustomerButWithoutContact > 0) {
        if (message) message += '<hr>';
        message += countOrdersWithCustomerButWithoutContact > 1
          ? `${countOrdersWithCustomerButWithoutContact} pedidos estão sem contato de cliente e não tem como notificar.`
          : `Um pedido está sem contato de cliente e não tem como notificar.`;
      }
      if (countOrdersWithStateIneligibleForNotification > 0) {
        if (message) message += '<hr>';
        message += countOrdersWithStateIneligibleForNotification > 1
          ? `${countOrdersWithStateIneligibleForNotification} pedidos estão em estado avançado e não faz sentido notificar.`
          : `Um pedido está em estado avançado e não faz sentido notificar.`;
      }

      if (countOrdersWithNumberOfNotificationsOnLimit > 0) {
        if (message) message += '<hr>';
        message += countOrdersWithNumberOfNotificationsOnLimit > 1
          ? `${countOrdersWithNumberOfNotificationsOnLimit} pedidos já possuem um número alto de notificações e devem ser tratados com cautela.`
          : `Um pedido já possui um número alto de notificações e deve ser tratado com cautela.`;
      }

      if (countElibibleOrders > 0) {
        if (message) message += '<hr>';
        message += countElibibleOrders > 1
          ? `De ${countSelectedOrders} pedidos ${countElibibleOrders} são válidos para notificação.`
          : `De ${countSelectedOrders} pedidos um é válido para notificação.`
        message += '<hr>Deseja continuar?';
        const response = await this.alertService.confirmHtml(message);
        if (!response.isConfirmed) return null;
      } else {
        message += '<hr>Todos os pedidos possuem alguma restrição e não tem como prosseguir com a notificação.';
        this.alertService.warningHtml(message);
        return null;
      }
    }

    return elibibleOrders;
  }

  getFirstRecipeApprovedAt = (orderState: OrderStateOutput) => {
    if (orderState.order.separationType !== SeparationType.Prescription) return null;

    const firstRecipe = orderState.recipe && orderState.recipe.length > 0 ? orderState.recipe[0] : undefined;
    const firstCustomerRecipe = orderState.customerRecipes && orderState.customerRecipes.length > 0 ? orderState.customerRecipes[0] : undefined;

    let firstRecipeApprovedAt = firstRecipe?.recipeApprovedAt || firstCustomerRecipe?.recipeApprovedAt;

    if (firstRecipeApprovedAt && firstRecipe?.recipeApprovedAt && firstRecipe.recipeApprovedAt > firstRecipeApprovedAt)
      firstRecipeApprovedAt = firstRecipe.recipeApprovedAt;

    if (firstRecipeApprovedAt && firstCustomerRecipe?.recipeApprovedAt && firstCustomerRecipe.recipeApprovedAt > firstRecipeApprovedAt)
      firstRecipeApprovedAt = firstCustomerRecipe.recipeApprovedAt;

    const firstProductRecipeLinkingRequest = orderState.productRecipeLinkingRequests && orderState.productRecipeLinkingRequests.length > 0
      ? orderState.productRecipeLinkingRequests[0] : undefined;
    if (firstProductRecipeLinkingRequest)
      firstRecipeApprovedAt = firstProductRecipeLinkingRequest.updatedAt && firstProductRecipeLinkingRequest.updatedAt > firstProductRecipeLinkingRequest.createdAt
        ? firstProductRecipeLinkingRequest.updatedAt
        : firstProductRecipeLinkingRequest.createdAt;

    return firstRecipeApprovedAt;
  }

  orderStateToOrderForListing = (orderState: OrderStateOutput) => {
    const orderForListing = new OrderForListing();
    orderForListing.order = orderState.order;
    orderForListing.stateLog = orderState.stateLog;
    if (orderState.stateLog?.currentState) {
      orderForListing.currentStateName = orderState.stateLog?.currentState?.description;
    }
    if (!orderForListing.currentStateName) {
      orderForListing.currentStateName = orderState.stateLog
        ? this.states.find(x => x.id === orderState.stateLog?.currentStateId)?.name ||
        this.deliveryStates.find(x => x.id === orderState.stateLog?.currentStateId)?.name || ''
        : '';
    }
    orderForListing.statePrescriptionLog = orderState.statePrescriptionLog;
    if (orderForListing.statePrescriptionLog && orderState.statePrescriptionLog && !orderState.statePrescriptionLog.currentState) {
      orderForListing.statePrescriptionLog.currentState = {
        id: orderState.statePrescriptionLog.currentStateId,
        description: this.statesPrescription.find(x => x.id === orderState.statePrescriptionLog?.currentStateId)?.description || ''
      };
    }
    orderForListing.currentStatePrescriptionName = orderState.statePrescriptionLog?.currentState?.description || '';
    orderForListing._selected = false;
    orderForListing.recipe = orderState.recipe;
    orderForListing.customerRecipes = orderState.customerRecipes;
    orderForListing.erpOrders = orderState.erpOrders;
    orderForListing.influencerOrder = orderState.order.tags?.includes(strings.TagForInfluencers);
    orderForListing.orderWithPrescriptionEyeGlassesForDifferentPersons =
      orderState.order.separationType === SeparationType.Prescription && !!orderState.order.note &&
      orderState.order.note.includes(strings.NoteForPrescriptionGlassesForDifferentPeoples);
    orderForListing.nonProgressivePrescriptionOrderWithMoreThanOneLens =
      orderState.order.separationType === SeparationType.Prescription && !!orderState.order.tags &&
      orderState.order.tags.includes(orderTags.non_progressive_order_bf2023.value);
    orderForListing.daysSinceCreation = orderState.daysSinceCreation;
    orderForListing.daysSincePayment = orderState.daysSincePayment;
    orderForListing.daysSinceRecipeApproval = orderState.daysSinceRecipeApproval;
    orderForListing.daysSinceNpdApproval = orderState.daysSinceNpdApproval;
    orderForListing.nonWorkingDaysSincePayment = orderState.nonWorkingDaysSincePayment;
    orderForListing.nonWorkingDaysSinceRecipeApproval = orderState.nonWorkingDaysSinceRecipeApproval;
    orderForListing.nonWorkingDaysSinceNpdApproval = orderForListing.nonWorkingDaysSinceNpdApproval;
    orderForListing.firstRecipeApprovedAt = this.getFirstRecipeApprovedAt(orderState);
    orderForListing.allLensesArePlan = orderState.allLensesArePlan;
    orderForListing.changeFromNonPrescriptionToPrescription = orderState.changeFromNonPrescriptionToPrescription;
    orderForListing.changeFromPrescriptionToNonPrescription = orderState.changeFromPrescriptionToNonPrescription;
    orderForListing.hasShippingLines = orderState.hasShippingLines;
    orderForListing.clientDelayDaysSinceBaseDateForDeliveryDeadline = orderState.clientDelayDaysSinceBaseDateForDeliveryDeadline;
    orderForListing.internalDelayDaysSinceBaseDateForDeliveryDeadline = orderState.internalDelayDaysSinceBaseDateForDeliveryDeadline;
    orderForListing._externallyChanged = orderState?.order?.tags?.includes(orderTags.externally_changed.value);
    orderForListing._preSaleSport = false;
    const tags = (orderState?.order?.tags || '').trim();
    if (tags) {
      orderForListing._preSaleSport = tags == orderTags.pre_sale_sport.value;
      if (!orderForListing._preSaleSport) {
        const extraValues = [`{value},`, `, {value}`, ` {value}`, `,{value}`];
        extraValues.forEach(content => {
          const contentValue = content.replace('{value}', orderTags.pre_sale_sport.value);
          if (tags.includes(contentValue)) {
            orderForListing._preSaleSport = true;
          }
        });
      }
    }

    if (orderForListing.influencerOrder)
      orderForListing._informations.push('Pedido por influenciadores.');
    if (orderForListing.changeFromNonPrescriptionToPrescription || orderForListing.changeFromPrescriptionToNonPrescription)
      orderForListing._informations.push('Pedido com tipo trocado.');
    if (orderForListing._externallyChanged)
      orderForListing._informations.push('Pedido com alteração externa.');
    if (orderForListing._preSaleSport) {
      orderForListing._informations.push('Pré-venda sport.');
    }

    if (!orderForListing.order.customer) {
      orderForListing._alerts.push(
        {
          type: OrderAlertType.NoCustumer,
          value: strings.AlertForClientNotInformed
        }
      );
    }

    if (orderForListing.order.financialStatus == FinancialStatus.Pending ||
      orderForListing.order.financialStatus == FinancialStatus.PartiallyPaid
    ) {
      orderForListing._alerts.push(
        {
          type: OrderAlertType.PaidPending,
          value: 'Pagamento pendente'
        }
      );
    }

    orderForListing.lensMaterialList = orderState.lensMaterialList;
    orderForListing.lensMaterialName = orderState.lensMaterialName;
    orderForListing.originLocationForCrossChannelSale = orderState.originLocationForCrossChannelSale;

    orderForListing.omniSummary = orderState.omniSummary;
    const countOfOtherOpenOmniOrders = orderForListing.omniSummary?.countOfOtherOpenOmniOrders || 0;
    if (countOfOtherOpenOmniOrders > 0) {
      orderForListing._alerts.push(
        {
          type: OrderAlertType.OmniOrderWithOtherOpen,
          value: countOfOtherOpenOmniOrders > 1 
            ? `Este cliente tem ${countOfOtherOpenOmniOrders} outros pedidos omni em aberto.`
            : `Este cliente tem outro pedido omni em aberto.`
        }
      );
    }

    return orderForListing;
  }

  getUserConfirmationBeforeShowModalToUpdateStateByGlobalPickerForSelectedOrders = async (selectedOrders: OrderForListing[]) => {
    const countCancelledOrders = this.getCountOfCancelledAndNotClosedOrders(selectedOrders);
    const countClosedOrders = this.getCountOfClosedOrders(selectedOrders);
    const countOrdersWithAnyVendorPendency = this.getCountOrdersWithAnyVendorPendency(selectedOrders);

    if (countCancelledOrders === 0 && countClosedOrders === 0 && countOrdersWithAnyVendorPendency === 0)
      return true;

    let message = '';
    if (countCancelledOrders > 0) {
      message += countCancelledOrders > 1
        ? `${countCancelledOrders} pedidos selecionados estão cancelados e não serão alterados. `
        : `Um pedido selecionado está cancelado e não será alterado. `;
    }
    if (countClosedOrders > 0) {
      if (message) message += '<hr>';
      message += countClosedOrders > 1
        ? `${countClosedOrders} pedidos selecionados estão fechados e não serão alterados. `
        : `Um pedido selecionado está fechado e não será alterado. `;
    }
    if (countOrdersWithAnyVendorPendency > 0) {
      if (message) message += '<hr>';
      message += countOrdersWithAnyVendorPendency > 1
        ? `${countOrdersWithAnyVendorPendency} pedidos selecionados possuem alguma pendência com o vendedor e não serão alterados. `
        : `Um pedido selecionado possui pendência com o vendedor e não será alterado. `;
    }

    const countIneligibleOrders = countCancelledOrders + countClosedOrders + countOrdersWithAnyVendorPendency;

    if (countIneligibleOrders >= selectedOrders.length) {
      message += '<br><br><strong>Todos os pedidos selecionados possuem restrições de alteração e portanto não podem ser alterados.</strong>';
      await this.alertService.warningHtml(message);
      return false;
    } else {
      message += '<br><br><strong>Deseja continuar?</strong>';
      const response = await this.alertService.confirmHtml(message);
      return response.isConfirmed;
    }
  }

  getCountOrdersWithAnyVendorPendency = (selectedOrders: OrderForListing[]) =>
    selectedOrders.filter(xo =>
      !(xo.order.shopifyFormatedClosedAt?.length) &&
      !(xo.order.shopifyFormatedCancelledAt?.length) &&
      (
        xo.stateLog?.currentStateId === StateOrderIdentification.PendenteVendedor ||
        xo.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.SemReceita
      )
    ).length;

  getEligibleOrdersForUpdatingPerformedByGlobalPicker = async (selectedOrders: OrderForListing[]) => {
    const finalElibibleOrders: CurrentState[] = [];
    const elibibleOrders = await this.getEligibleOrdersToUpdate(selectedOrders);
    if (elibibleOrders && elibibleOrders.length > 0) {
      const pendingOrders: CurrentState[] = [];
      elibibleOrders.forEach((elibibleOrder) => {
        const isPending = elibibleOrder.currentStateLogId === StateOrderIdentification.PendenteVendedor ||
          elibibleOrder.currentStatePrescriptionLogId === StatePrescriptionIdentification.SemReceita;
        if (isPending) {
          pendingOrders.push(elibibleOrder);
        } else {
          finalElibibleOrders.push(elibibleOrder);
        }
      });
      if (finalElibibleOrders.length === 0) {
        const message = 'Todos os pedidos selecionados possuem restrições de alteração e portanto não podem ser alterados.';
        await this.alertService.warningHtml(message);
        return null;
      }
      const countPendingOrders = pendingOrders.length;
      let message = '';
      if (countPendingOrders > 0) {
        message += countPendingOrders > 1
          ? `${countPendingOrders} pedidos selecionados possuem alguma pendência com o vendedor e ao continuar estes pedidos não serão alterados. `
          : `Um pedido selecionado possui alguma pendência com o vendedor e ao continuar este pedido não será alterado. `;
      }
      message += '<br><br><strong>Deseja realmente continuar?</strong>';
      const response = await this.alertService.confirmHtml(message);
      if (!response.isConfirmed) return null;
    }
    return finalElibibleOrders;
  }

}